/**
 * dd-card2.js — interactive expanded product card
 *
 * The Liquid snippet `snippets/dd-product-card-expanded.liquid` emits a
 * <dd-card2> custom element with:
 *   - an embedded variants JSON payload
 *   - clickable length pills + color swatches with data-option-position
 *     + data-option-value attributes
 *   - a CTA button (data-cta) whose label + disabled state this component
 *     manages based on current selection
 *
 * The component:
 *   - Tracks selected option values by position (1, 2, 3)
 *   - Matches against the variants list to update hero image + price
 *     + selected-color label whenever a value is picked
 *   - Reflects Regios DOPP account tier / sitewide automatic discounts
 *     in the "Save X%" pill and price display by calling
 *     `window.RegiosDOPP.api.v0.productPage.calculateDiscountedPrices`
 *     per variant — mirrors the PDP logic in `dd-discount-pill.js`
 *   - Toggles the CTA between "Select Options" (disabled) and "Add to
 *     cart" (enabled) based on whether every "required" option position
 *     has been selected
 *   - On CTA click, adds the selected variant via window.DDCartApi and
 *     dispatches `cart:updated` + `cart:refresh` so the cart drawer
 *     refreshes without a page reload.
 */
(() => {
  if (window.customElements.get('dd-card2')) return;

  const ADDED_LABEL = 'Added';
  const ADDING_LABEL = 'Adding…';
  // Fallback only — the canonical label is set server-side via
  // `data-label-unavailable` in snippets/dd-product-card-expanded.liquid
  // ("Awaiting restock"). This default is used only when the snippet
  // is rendered without the data attr (legacy callers / tests).
  const UNAVAILABLE_LABEL_FALLBACK = 'Awaiting restock';

  /* ── Shared Regios state ────────────────────────────────────────────
     A single subscription listens for the Regios DOPP v0 API
     (via both the official `regios-dopp:api-initialized` event and a
     polling fallback) and notifies every card once it's ready.

     API choice: related-products cards render products that are NOT
     the current PDP product, so we use the generic
       api.v0.calculateDiscountedPrices({ productId, variantId, regularPriceInCents })
     form (see dd-cart-cross-sell.js + dd-mini-cart.js for prior art).
     `api.v0.productPage.calculateDiscountedPrices` is scoped to the
     current PDP and returns nothing useful for other products, which
     is why v1 of this integration appeared "silent".

     Cache is keyed by variantId since DOPP discounts are variant-
     level. A rail of 20 cards with duplicate variants only triggers
     one request per variant. */
  let regiosV0 = null;
  let regiosListening = false;
  const regiosSubscribers = new Set();
  const regiosCache = Object.create(null); // { [variantId]: {pct, saleCents, compareCents, name} | null }
  const regiosInFlight = Object.create(null); // { [variantId]: Promise<entry|null> }

  function onRegiosReady(v0) {
    regiosV0 = v0;
    regiosSubscribers.forEach((card) => card._onRegiosReady());
  }

  function startRegiosWatcher() {
    if (regiosListening) return;
    regiosListening = true;

    // Official DOPP API init event — fires once, regardless of load order.
    window.addEventListener('regios-dopp:api-initialized', function (e) {
      const v0 =
        (e && e.detail && e.detail.api) ||
        (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0);
      if (v0 && !regiosV0) onRegiosReady(v0);
    });

    // Poll fallback for scripts that attach the global without dispatching the event.
    if (window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0) {
      onRegiosReady(window.RegiosDOPP.api.v0);
      return;
    }
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      const v0 =
        window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0;
      if (v0) {
        clearInterval(timer);
        if (!regiosV0) onRegiosReady(v0);
      } else if (attempts >= 60) {
        clearInterval(timer);
      }
    }, 500);
  }

  function fetchRegios(productId, variantId, priceCents) {
    const vid = String(variantId);
    if (regiosCache[vid] !== undefined) return Promise.resolve(regiosCache[vid]);
    if (regiosInFlight[vid]) return regiosInFlight[vid];
    if (!regiosV0 || typeof regiosV0.calculateDiscountedPrices !== 'function') {
      return Promise.resolve(null);
    }

    const p = regiosV0
      .calculateDiscountedPrices({
        productId: parseInt(productId, 10),
        variantId: parseInt(vid, 10),
        regularPriceInCents: parseInt(priceCents, 10),
      })
      .then((result) => {
        if (!result || !result.salePrice || !result.regularPrice) {
          regiosCache[vid] = null;
          return null;
        }
        const saleAmt = parseFloat(result.salePrice.amount);
        const regAmt = parseFloat(result.regularPrice.amount);
        if (!(saleAmt < regAmt)) {
          regiosCache[vid] = null;
          return null;
        }
        const saleCents = Math.round(saleAmt * 100);
        const compareCents = Math.round(regAmt * 100);
        const pct = Math.floor(((compareCents - saleCents) * 100) / compareCents);

        // Extract a plain-text discount name from Regios' HTML payload.
        let name = '';
        const html =
          (result.description && result.description.html) ||
          result.discountMessage ||
          '';
        if (html) {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          name = (tmp.textContent || tmp.innerText || '').trim();
        }

        const entry = { pct, saleCents, compareCents, name };
        regiosCache[vid] = entry;
        return entry;
      })
      .catch(() => {
        // Silent — Liquid baseline stays visible as the fallback.
        return null;
      })
      .finally(() => {
        delete regiosInFlight[vid];
      });

    regiosInFlight[vid] = p;
    return p;
  }

  function formatMoney(cents) {
    const fmt = window.shopMoneyFormat
      ? String(window.shopMoneyFormat).replace(/<[^>]*>/g, '')
      : '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const out = fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    // Match Liquid's `money_without_trailing_zeros`: strip ".00" / ",00"
    // but keep non-zero decimals and trailing currency codes intact.
    return out.replace(/([.,])00(?!\d)/, '');
  }

  class DdCard2 extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.variants = this._readVariants();
      this.requiredPositions = this._readRequiredPositions();
      this.lengthPosition = Number(this.dataset.lengthPosition) || null;
      this.colorPosition = Number(this.dataset.colorPosition) || null;
      this.labels = {
        select: this.dataset.labelSelect || 'Select Options',
        add: this.dataset.labelAdd || 'Add to cart',
        unavailable: this.dataset.labelUnavailable || UNAVAILABLE_LABEL_FALLBACK,
      };

      this.selection = { 1: null, 2: null, 3: null };
      this._applyInitialSelection();

      this.heroEl = this.querySelector('[data-hero-image]');
      // Sibling <img> emitted only when the product has > 1 image. Capture
      // its initial src so we can revert to the product-level lifestyle
      // shot for variants that don't carry their own second image.
      this.hoverImageEl = this.querySelector('[data-hover-image]');
      this.defaultHoverSrc = this.hoverImageEl ? this.hoverImageEl.getAttribute('src') || '' : '';
      this.priceEl = this.querySelector('[data-price]');
      this.colorLabelEl = this.querySelector('[data-selected-color]');
      this.colorBadgeEl = this.querySelector('[data-color-badge]');
      this.saleBadgeEl = this.querySelector('[data-sale-badge]');
      this.saleBadgeLabelEl = this.querySelector('[data-sale-badge-label]');
      this.ctaEl = this.querySelector('[data-cta]');
      this.ctaLabelEl = this.querySelector('[data-cta-label]');

      // Quantity stepper — value lives on the card instance, the DOM
      // element only mirrors it for display. Starts at 1 and is clamped
      // to >= 1; upper bound is enforced by `_variantMaxQty` against
      // the resolved variant's `inventory_management`/`inventory_policy`
      // /`inventory_quantity` triplet (Liquid-seeded + GraphQL-refreshed).
      this.qtyValueEl = this.querySelector('[data-qty-value]');
      this.qtyDecEl = this.querySelector('[data-qty-decrement]');
      this.qtyIncEl = this.querySelector('[data-qty-increment]');
      // Stock-cap toast under the qty pill. Same element is reused
      // across show/hide cycles; only `textContent` + the
      // `is--visible` class change. `_qtyToastHideTimer` lets a fresh
      // overflow event reset the auto-dismiss window.
      this.qtyToastEl = this.querySelector('[data-qty-toast]');
      this._qtyToastHideTimer = null;
      this.quantity = 1;
      this._updateQtyUi();

      this.productId = this.dataset.productId || '';

      this.addEventListener('click', (event) => this._onClick(event));

      // Subscribe to the shared Regios watcher — `_onRegiosReady` will
      // fire once the DOPP v0 API lands so this card can re-price.
      regiosSubscribers.add(this);
      startRegiosWatcher();
      if (regiosV0) this._onRegiosReady();

      this._refresh();

      // Variant hydration policy:
      //   - Bundle-mode cards (data-dd-cta-mode="bundle") ALWAYS hit
      //     Storefront GraphQL on first paint to refresh availability +
      //     stock + price. The bundle UX represents large multi-item
      //     trade orders; stale "available" flags translate directly
      //     into bad checkout outcomes.
      //   - Non-bundle cards with a capped seed (data-variants-capped
      //     ="true") hydrate eagerly too — they're missing variants,
      //     not just stale, so eager fetch is correctness-mandatory.
      //   - All other non-bundle cards (the common PLP case) hydrate
      //     LAZILY on first shopper intent (hover / focus / touch) so
      //     a 16-card collection page doesn't fire 16 storefront
      //     queries on load. The loader is memoized per-handle so
      //     re-visits cost nothing. See `_scheduleLazyInventoryHydration`.
      // Either way, _loadFullVariants() runs AFTER the initial
      // _refresh() so first paint is identical to the seeded state;
      // GraphQL then silently overrides seed availability + price
      // where they differ (and ALWAYS overlays inventory_quantity /
      // inventory_policy / inventory_management — the qty cap rides
      // on that triplet), and a second _refresh() corrects any
      // selection or qty state that pointed at now-sold-out variants.
      const isBundleMode = this.dataset.ddCtaMode === 'bundle';
      if (isBundleMode || this.dataset.variantsCapped === 'true') {
        this._loadFullVariants({ refreshSeed: isBundleMode });
      } else {
        this._scheduleLazyInventoryHydration();
      }
    }

    /**
     * Defer the Storefront GraphQL inventory refresh until the shopper
     * shows intent — first hover (desktop), first focus (keyboard),
     * or first touch (mobile). This keeps PLP / collection / search
     * pages at 0 Shopify requests on initial load (per the
     * rate-limit-awareness rule's "non-cart page load: 0-1" budget),
     * while still giving every card that the shopper actually
     * engages with a fresh `inventory_quantity` for the qty cap.
     *
     * The three listeners share a single trigger and explicitly tear
     * down the other two on first fire (`{ once: true }` only
     * removes the listener that fired, not its siblings). After
     * `_loadFullVariants` runs, the per-handle memoization inside
     * ddVariantLoader makes any subsequent calls free — re-hovering
     * the same card, or hovering a different card on the same
     * product, both reuse the cached response.
     *
     * The hydration uses `refreshSeed: false`. That means seeded
     * `available` / `price` / `compareAt` stay put (avoiding a
     * PLP-wide price flicker on slow networks) but `inventory_*`
     * always overlays — the always-on overlay branch inside
     * `_loadFullVariants` ignores `refreshSeed`. This is the
     * minimum change to make the qty cap reflect live stock.
     */
    _scheduleLazyInventoryHydration() {
      if (this._inventoryHydrationScheduled) return;
      this._inventoryHydrationScheduled = true;

      const trigger = () => {
        this.removeEventListener('pointerenter', trigger);
        this.removeEventListener('focusin', trigger);
        this.removeEventListener('touchstart', trigger);
        this._loadFullVariants({ refreshSeed: false });
      };

      this.addEventListener('pointerenter', trigger, { once: true, passive: true });
      this.addEventListener('focusin', trigger, { once: true });
      this.addEventListener('touchstart', trigger, { once: true, passive: true });
    }

    disconnectedCallback() {
      regiosSubscribers.delete(this);
    }

    /** Called by the shared watcher once the Regios DOPP v0 API is ready. */
    _onRegiosReady() {
      const v = this._matchVariant();
      if (v && v.id) this._ensureRegios(v);
    }

    /**
     * Kick off a Regios fetch for the given variant if not cached /
     * in-flight. When a tier or automatic discount lands, re-run
     * `_refresh()` so the price + pill update.
     */
    _ensureRegios(variant) {
      if (!variant || !variant.id || !this.productId) return;
      if (!regiosV0) return;
      const vid = String(variant.id);
      if (regiosCache[vid] !== undefined) return;
      const priceCents = typeof variant.priceCents === 'number' ? variant.priceCents : 0;
      if (!priceCents) return;
      fetchRegios(this.productId, variant.id, priceCents).then((entry) => {
        // Only refresh if this card still cares about that variant.
        const current = this._matchVariant();
        if (current && String(current.id) === vid && entry != null) {
          this._refresh();
        }
      });
    }

    _readVariants() {
      const script = this.querySelector('script[data-variants-json]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (err) {
        console.warn('[dd-card2] invalid variants JSON', err);
        return [];
      }
    }

    /**
     * Hydrate `this.variants` from Storefront GraphQL via
     * `window.ddVariantLoader.fetch(handle)`. Two callsites use this:
     *
     *   1. Capped-seed correction (refreshSeed: false) — when Liquid's
     *      `product.variants` is missing entries past the platform cap,
     *      we APPEND the missing variants. Seeded entries are
     *      untouched (their `available` + price stay as Liquid
     *      rendered them, which is acceptable for the "fix the gap"
     *      case).
     *
     *   2. Live freshness (refreshSeed: true, used by bundle-mode
     *      cards) — GraphQL becomes ground truth for `available` +
     *      `priceCents` + `compareAtCents` on EVERY variant. Seed
     *      values are overridden because trade-bundle decisions ride
     *      on inventory accuracy and the seed is only as fresh as
     *      page-render time. Image data stays from seed (the seed's
     *      `variant_image` metafield resolution is richer than the
     *      loader's plain `image_url`).
     *
     * Defensive guards mirror dd-swatch-selector:
     *  - Loader script absent → no-op
     *  - Already loaded once → no-op (loader has its own 5min cache)
     *  - In-flight → no-op (prevents racing fetches)
     *  - No product handle → no-op (snippet misconfiguration)
     *  - Loader returns [] (failure) → never overwrite real data
     */
    _loadFullVariants(opts) {
      const refreshSeed = !!(opts && opts.refreshSeed);
      if (!window.ddVariantLoader) return;
      if (this._fullVariantsLoaded) return;
      if (this._fullVariantsInflight) return;
      const handle = this.dataset.productHandle;
      if (!handle) return;

      this._fullVariantsInflight = true;
      window.ddVariantLoader.fetch(handle).then((full) => {
        this._fullVariantsInflight = false;
        if (!Array.isArray(full) || full.length === 0) {
          // Loader failed — leave the seed untouched. Never mark
          // _fullVariantsLoaded so a subsequent retry can succeed.
          return;
        }

        // Index seeded variants by ID for two purposes:
        //  - skip seeded IDs when appending past-cap additions
        //  - quickly locate seeded entry to overwrite in refresh mode
        const seededIndex = Object.create(null);
        for (let i = 0; i < this.variants.length; i++) {
          seededIndex[String(this.variants[i].id)] = i;
        }

        // Track whether anything changed so we can skip _refresh()
        // when GraphQL returned data byte-identical to the seed.
        let changed = false;
        const additions = [];

        for (let i = 0; i < full.length; i++) {
          const lv = full[i];
          if (!lv || lv.id == null) continue;
          const idKey = String(lv.id);
          const cents = typeof lv.price === 'number' ? lv.price : 0;
          const compareCents =
            typeof lv.compare_at_price === 'number' && lv.compare_at_price > cents
              ? lv.compare_at_price
              : null;
          const available = !!lv.available;

          // Inventory fields from the loader (Storefront GraphQL inferred
          // shape — see ddVariantLoader.js for the inventoryPolicy
          // inference). Carried onto every variant so `_variantMaxQty`
          // can cap the qty stepper without a second lookup.
          const invQty = typeof lv.inventory_quantity === 'number' ? lv.inventory_quantity : null;
          const invPolicy = lv.inventory_policy || null;
          const invMgmt = lv.inventory_management || null;

          if (seededIndex[idKey] !== undefined) {
            // Existing seeded variant. In refresh mode, overlay
            // availability + pricing + inventory from GraphQL (ground
            // truth) and leave image/options/hover alone (seed is
            // richer). Inventory is overlaid in BOTH modes (seed too)
            // because Liquid's `v.inventory_quantity` is fresh enough
            // at first paint but quickly drifts; GraphQL is the
            // authority once it lands.
            const seedVar = this.variants[seededIndex[idKey]];
            if (refreshSeed) {
              if (
                seedVar.available !== available
                || seedVar.priceCents !== cents
                || seedVar.compareAtCents !== compareCents
              ) {
                seedVar.available = available;
                seedVar.priceCents = cents;
                seedVar.price = '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
                seedVar.compareAtCents = compareCents;
                seedVar.compareAt = compareCents
                  ? '$' + (compareCents / 100).toFixed(2).replace(/\.00$/, '')
                  : null;
                changed = true;
              }
            }
            // Always overlay inventory — seed is correct at first paint
            // but the loader has the more current snapshot, and the qty
            // cap rides on it.
            if (
              seedVar.inventory_quantity !== invQty
              || seedVar.inventory_policy !== invPolicy
              || seedVar.inventory_management !== invMgmt
            ) {
              seedVar.inventory_quantity = invQty;
              seedVar.inventory_policy = invPolicy;
              seedVar.inventory_management = invMgmt;
              changed = true;
            }
            continue;
          }

          // Not in seed — append, normalizing loader's flat shape into
          // the nested options/price shape dd-card2 expects internally.
          additions.push({
            id: lv.id,
            available: available,
            options: [
              lv.option1 || '',
              lv.option2 || '',
              lv.option3 || '',
            ],
            price: '$' + (cents / 100).toFixed(2).replace(/\.00$/, ''),
            priceCents: cents,
            compareAt: compareCents
              ? '$' + (compareCents / 100).toFixed(2).replace(/\.00$/, '')
              : null,
            compareAtCents: compareCents,
            inventory_quantity: invQty,
            inventory_policy: invPolicy,
            inventory_management: invMgmt,
            image: lv.image_url || null,
            hoverImage: lv.image_url || null,
          });
          changed = true;
        }

        if (additions.length > 0) {
          this.variants = this.variants.concat(additions);
        }
        this._fullVariantsLoaded = true;

        if (changed) {
          // Re-refresh so any selection now resolves to fresh
          // availability/price + any past-cap variant becomes
          // selectable + sold-out variants flip to disabled state.
          this._refresh();
        }
      }).catch((err) => {
        this._fullVariantsInflight = false;
        try { console.warn('[dd-card2] variant loader failed', err && err.message); } catch (_e) { /* */ }
      });
    }

    _readRequiredPositions() {
      const raw = (this.dataset.requiredPositions || '').trim();
      if (!raw) return [];
      return raw
        .split(',')
        .map((n) => Number(n))
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 3);
    }

    /**
     * Apply any server-seeded selections (e.g. an auto-hidden "Texture:
     * Straight" for SecreTapes / Keratin Tips / SuperWefts). These
     * selections are not rendered as pills/swatches but still count
     * toward variant matching, so the shopper never has to pick them.
     */
    _applyInitialSelection() {
      const raw = this.dataset.initialSelection;
      if (!raw) return;
      try {
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return;
        arr.forEach((entry) => {
          if (!entry) return;
          const pos = Number(entry.position);
          const value = typeof entry.value === 'string' ? entry.value : '';
          if (pos >= 1 && pos <= 3 && value) {
            this.selection[pos] = value;
          }
        });
      } catch (err) {
        console.warn('[dd-card2] invalid initial selection', err);
      }
    }

    _onClick(event) {
      // Qty stepper — handled before the generic option-button check so the
      // −/+ buttons (which don't carry option data attrs) intercept cleanly.
      const qtyBtn = event.target.closest('[data-qty-decrement], [data-qty-increment]');
      if (qtyBtn && this.contains(qtyBtn)) {
        event.preventDefault();
        if (qtyBtn.hasAttribute('disabled')) return;
        const dir = qtyBtn.hasAttribute('data-qty-increment') ? 1 : -1;
        this._setQuantity(this.quantity + dir);
        return;
      }

      const optionBtn = event.target.closest('[data-option-position][data-option-value]');
      if (optionBtn) {
        if (optionBtn.getAttribute('data-available') === 'false') return;
        event.preventDefault();
        const pos = Number(optionBtn.getAttribute('data-option-position'));
        const val = optionBtn.getAttribute('data-option-value');
        this._select(pos, val);
        return;
      }

      // Swatch-overflow expand chip. The actual expansion (adding
      // `.is-expanded`) is handled at document-level in dd-card-rating-
      // hydrate.js — we don't fight it. We just take the opportunity
      // to re-sync availability so the freshly-revealed swatches reflect
      // the current length selection without waiting for the next
      // option click or GraphQL refresh.
      const moreBtn = event.target.closest('[data-swatch-more]');
      if (moreBtn) {
        // Defer one tick so the document-level handler has flipped
        // `.is-expanded` before we measure / paint downstream state.
        const self = this;
        setTimeout(function () { self._syncColorSwatchAvailability(); }, 0);
      }

      const cta = event.target.closest('[data-cta]');
      if (cta && cta === this.ctaEl) {
        if (this.ctaEl.hasAttribute('disabled')) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        this._addToCart();
      }
    }

    /**
     * Resolve the in-stock cap for the currently-selected variant.
     *
     *   Infinity → no cap (no variant matched, oversell policy, or
     *              inventory untracked)
     *   number   → hard cap (variant tracks inventory + `deny` policy)
     *
     * Three signals decide:
     *   • `inventory_management === null` → variant doesn't track stock.
     *     Don't cap.
     *   • `inventory_policy === 'continue'` → tracked but oversell
     *     allowed (preorder / continue-selling). Don't cap.
     *   • Otherwise → cap at `inventory_quantity`, floored at 0 so a
     *     negative count (rare but possible after sync drift) reads
     *     as "no more can be added."
     *
     * Source: ddVariantLoader.js (GraphQL hydration) writes these
     * three fields onto every variant. The Liquid seed in
     * dd-product-card-expanded.liquid also emits them so first-paint
     * carts work before GraphQL lands.
     */
    _variantMaxQty(variant) {
      if (!variant) return Infinity;
      if (!variant.inventory_management) return Infinity;
      if (variant.inventory_policy === 'continue') return Infinity;
      if (typeof variant.inventory_quantity !== 'number') return Infinity;
      return Math.max(0, variant.inventory_quantity);
    }

    _setQuantity(next) {
      const max = this._variantMaxQty(this._matchVariant());
      // `Math.min(x, Infinity)` returns x — so the cap is transparent
      // for untracked / oversell variants.
      const requested = Math.floor(Number(next) || 1);
      const clamped = Math.max(1, Math.min(max, requested));

      // Cap-overflow signal — the shopper just asked for more than
      // we can sell. This fires the toast even if `clamped` ends up
      // equal to the current quantity (re-tapping `+` at cap).
      if (Number.isFinite(max) && requested > max) {
        this._showQtyToast(
          'Only ' + max + ' ' + (max === 1 ? 'is' : 'are') + ' in stock right now'
        );
      } else if (clamped < this.quantity) {
        // Shopper is actively dec'ing — any lingering cap toast is
        // stale, retire it.
        this._hideQtyToast();
      }

      if (clamped === this.quantity) return;
      this.quantity = clamped;
      this._updateQtyUi();
    }

    _updateQtyUi() {
      if (this.qtyValueEl) this.qtyValueEl.textContent = String(this.quantity);
      // Decrement is disabled at the floor so shoppers can't tap below 1.
      // Keeps the native `disabled` attribute — going below 1 is purely
      // a mechanical floor with no informational message to surface.
      if (this.qtyDecEl) {
        if (this.quantity <= 1) {
          this.qtyDecEl.setAttribute('disabled', '');
          this.qtyDecEl.setAttribute('aria-disabled', 'true');
        } else {
          this.qtyDecEl.removeAttribute('disabled');
          this.qtyDecEl.setAttribute('aria-disabled', 'false');
        }
      }
      // Increment at the in-stock cap: use ONLY `aria-disabled` (no
      // native `disabled` attribute). Native `disabled` suppresses
      // click events entirely — but we *want* the click to reach
      // `_onClick → _setQuantity → _showQtyToast` so the shopper sees
      // a "Only X in stock" message instead of a silent dead tap.
      // CSS already styles `[aria-disabled='true']` with opacity + the
      // not-allowed cursor, so the visual affordance is unchanged.
      if (this.qtyIncEl) {
        const max = this._variantMaxQty(this._matchVariant());
        if (this.quantity >= max) {
          this.qtyIncEl.setAttribute('aria-disabled', 'true');
          this.qtyIncEl.setAttribute('title', 'No more in stock');
        } else {
          this.qtyIncEl.removeAttribute('aria-disabled');
          this.qtyIncEl.removeAttribute('title');
        }
      }
    }

    /**
     * Show the stock-cap toast under the qty pill.
     *
     * Two RAFs before adding `is--visible` is the standard pattern for
     * triggering a CSS transition right after a textContent write — the
     * first RAF lets the layout settle with the new text (and unhides
     * a previously-faded toast at opacity 0), the second RAF runs
     * after the browser has painted the "from" state, so the
     * transition actually animates instead of jumping to the end.
     *
     * `_qtyToastHideTimer` is reset on every show so spamming the `+`
     * button keeps the toast on screen rather than flickering it off
     * mid-tap-sequence.
     */
    _showQtyToast(message) {
      const el = this.qtyToastEl;
      if (!el) return;
      el.textContent = message;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.classList.add('is--visible');
        });
      });
      if (this._qtyToastHideTimer) clearTimeout(this._qtyToastHideTimer);
      this._qtyToastHideTimer = setTimeout(() => this._hideQtyToast(), 2500);
    }

    _hideQtyToast() {
      const el = this.qtyToastEl;
      if (!el) return;
      el.classList.remove('is--visible');
      if (this._qtyToastHideTimer) {
        clearTimeout(this._qtyToastHideTimer);
        this._qtyToastHideTimer = null;
      }
      // Leave `textContent` in place — it'll be overwritten on the
      // next show, and an invisible-but-non-empty toast costs nothing.
      // Stripping it would defeat aria-live announcements anyway.
    }

    _select(position, value) {
      // Toggle-off if the same value is tapped twice.
      if (this.selection[position] === value) {
        this.selection[position] = null;
      } else {
        this.selection[position] = value;
      }
      this._refresh();
    }

    /**
     * Returns the variant that matches the current partial selection.
     * Falls back to the first available variant when nothing is selected.
     */
    _matchVariant() {
      const sel = this.selection;
      const matchesPartial = (v) => {
        for (let pos = 1; pos <= 3; pos++) {
          if (sel[pos] != null && v.options[pos - 1] !== sel[pos]) {
            return false;
          }
        }
        return true;
      };
      // Prefer an exact match that is also available.
      const partial = this.variants.filter(matchesPartial);
      if (partial.length === 0) return null;
      const available = partial.find((v) => v.available);
      return available || partial[0];
    }

    _isSelectionComplete() {
      return this.requiredPositions.every((pos) => this.selection[pos] != null);
    }

    _refresh() {
      this._updateOptionButtons();
      const variant = this._matchVariant();
      const pricing = this._computePricing(variant);
      this._updateHero(variant);
      this._updatePrice(variant, pricing);
      this._updateSaleBadge(pricing);
      this._updateColorLabel();
      this._updateCta(variant);

      // Clamp the qty stepper to the new variant's in-stock cap.
      // Two cases:
      //   • The shopper was at 10 of a deep-stock color (cap=Infinity)
      //     and switches to a color with cap=4 → clamp 10 → 4.
      //   • Initial paint / GraphQL just landed inventory data and the
      //     prior cap was Infinity → re-evaluate.
      // Always call _updateQtyUi at the end so the +/- button states
      // re-evaluate even when this.quantity didn't change (e.g. a
      // shopper is at 1 and switches between two in-stock variants).
      // If the clamp had to fire, also surface the toast so the
      // shopper understands why their counter dropped.
      const max = this._variantMaxQty(variant);
      if (this.quantity > max) {
        this.quantity = Math.max(1, max);
        if (Number.isFinite(max)) {
          this._showQtyToast(
            'Only ' + max + ' ' + (max === 1 ? 'is' : 'are') + ' in stock right now'
          );
        }
      }
      this._updateQtyUi();

      // Lazy-load Regios data for this variant if we haven't yet.
      if (variant) this._ensureRegios(variant);
    }

    /**
     * Produce the effective pricing for a variant by layering sources
     * in the same priority the PDP's `dd-discount-pill.js` uses:
     *   1. Baseline: variant.compareAt vs. variant.price (MSRP markdown)
     *   2. Regios DOPP override: account tier + sitewide automatic
     *      discounts that calculate a lower sale price than baseline
     * Returns `null` when no variant is matched.
     */
    _computePricing(variant) {
      if (!variant) return null;

      const priceCents = typeof variant.priceCents === 'number' ? variant.priceCents : null;
      const compareCents =
        typeof variant.compareAtCents === 'number' && variant.compareAtCents > priceCents
          ? variant.compareAtCents
          : null;

      let saleCents = priceCents;
      let effectiveCompareCents = compareCents;
      let pct = compareCents
        ? Math.floor(((compareCents - priceCents) * 100) / compareCents)
        : 0;
      let discountName = '';
      let saleText = variant.price || '';
      let compareText = variant.compareAt || '';
      let source = compareCents ? 'compareAt' : 'none';

      // Layer Regios on top if it produces a deeper discount than MSRP.
      const vid = variant.id != null ? String(variant.id) : null;
      if (vid && regiosCache[vid]) {
        const r = regiosCache[vid];
        if (r && r.pct > pct) {
          pct = r.pct;
          saleCents = r.saleCents;
          effectiveCompareCents = r.compareCents;
          discountName = r.name || '';
          saleText = formatMoney(r.saleCents);
          compareText = formatMoney(r.compareCents);
          source = 'regios';
        }
      }

      return {
        onSale: pct > 0 && effectiveCompareCents != null,
        pct,
        saleCents,
        compareCents: effectiveCompareCents,
        saleText,
        compareText,
        discountName,
        source,
      };
    }

    /**
     * Sync the "Save X%" pill with the effective pricing. Percentage +
     * visual language mirror `.dd-discount-pill-banner` on the PDP.
     */
    _updateSaleBadge(pricing) {
      if (!this.saleBadgeEl) return;
      if (!pricing || !pricing.onSale) {
        this.saleBadgeEl.setAttribute('hidden', '');
        return;
      }
      if (this.saleBadgeLabelEl) {
        this.saleBadgeLabelEl.textContent = `Save ${pricing.pct}%`;
      }
      this.saleBadgeEl.removeAttribute('hidden');
    }

    _updateOptionButtons() {
      const sel = this.selection;
      this.querySelectorAll('[data-option-position][data-option-value]').forEach((btn) => {
        const pos = Number(btn.getAttribute('data-option-position'));
        const val = btn.getAttribute('data-option-value');
        const isSelected = sel[pos] === val;
        btn.classList.toggle('dd-card2__length-pill--active', isSelected && btn.classList.contains('dd-card2__length-pill'));
        btn.classList.toggle('dd-card2__swatch--selected', isSelected && btn.classList.contains('dd-card2__swatch'));
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      // Per-color swatch availability — recompute against the currently
      // selected length (and any other non-color options). When a customer
      // picks 22" and only certain colors have stock at 22", the OOS
      // colors flip to the muted slash treatment. Re-runs after GraphQL
      // hydration too (because _loadFullVariants → _refresh →
      // _updateOptionButtons), so capped-seed cards reflect ground-truth
      // stock once the loader resolves.
      this._syncColorSwatchAvailability();
    }

    /**
     * Recompute availability for every rendered color swatch (collapsed
     * AND expanded), given the currently-selected non-color options.
     * Toggles `dd-card2__swatch--oos`, `data-available`, `aria-disabled`,
     * the `<span class="dd-card2__swatch-slash">` overlay, and the
     * visually-hidden "(out of stock)" suffix so the slash + a11y text
     * stay in sync. No-op when the card has no color option (single-
     * variant SKUs and length-only products).
     */
    _syncColorSwatchAvailability() {
      if (!this.colorPosition) return;
      const colorIdx = this.colorPosition - 1;
      const sel = this.selection;
      const swatches = this.querySelectorAll(
        '.dd-card2__swatch[data-option-position="' + this.colorPosition + '"][data-option-value]'
      );
      if (!swatches.length) return;
      const self = this;
      swatches.forEach((btn) => {
        const val = btn.getAttribute('data-option-value');
        if (!val) return;
        const available = self._isColorValueAvailable(val, colorIdx, sel);
        const wasOos = btn.classList.contains('dd-card2__swatch--oos');
        if (wasOos === !available) return;  // no DOM thrash when state is stable

        btn.classList.toggle('dd-card2__swatch--oos', !available);
        btn.setAttribute('data-available', available ? 'true' : 'false');

        if (available) {
          btn.removeAttribute('aria-disabled');
          const slash = btn.querySelector('.dd-card2__swatch-slash');
          if (slash) slash.remove();
        } else {
          btn.setAttribute('aria-disabled', 'true');
          if (!btn.querySelector('.dd-card2__swatch-slash')) {
            const slash = document.createElement('span');
            slash.className = 'dd-card2__swatch-slash';
            slash.setAttribute('aria-hidden', 'true');
            btn.insertBefore(slash, btn.firstChild);
          }
        }

        // Keep the visually-hidden a11y label in sync. SSR puts the
        // " (out of stock)" suffix inside the .visually-hidden span; we
        // append/remove it surgically so screen-reader text matches the
        // current visual state.
        const vh = btn.querySelector('.visually-hidden');
        if (vh) {
          const base = vh.textContent.replace(/\s*\(out of stock\)\s*$/, '').trim();
          vh.textContent = available ? base : base + ' (out of stock)';
        }
      });
    }

    /**
     * Does at least one variant exist that is `available` AND matches
     * `colorValue` at the color option position AND matches every
     * currently-selected non-color option?
     *
     * Walks `this.variants` once. After GraphQL hydration this array
     * has the full inventory grid, so the answer reflects live stock,
     * not just the 250-variant Liquid seed.
     */
    _isColorValueAvailable(colorValue, colorIdx, sel) {
      const vars = this.variants;
      if (!Array.isArray(vars) || !vars.length) return true;  // fail open
      for (let i = 0; i < vars.length; i++) {
        const v = vars[i];
        if (!v || !v.options) continue;
        if (v.options[colorIdx] !== colorValue) continue;
        let optionsMatch = true;
        for (let pos = 1; pos <= 3; pos++) {
          if (pos === this.colorPosition) continue;
          if (sel[pos] != null && v.options[pos - 1] !== sel[pos]) {
            optionsMatch = false;
            break;
          }
        }
        if (optionsMatch && v.available) return true;
      }
      return false;
    }

    _updateHero(variant) {
      // Primary image — swap to the variant's first image when we have
      // one. <img> vs placeholder <div> safety check preserved.
      if (variant && variant.image && this.heroEl && this.heroEl.tagName === 'IMG') {
        if (this.heroEl.getAttribute('src') !== variant.image) {
          this.heroEl.setAttribute('src', variant.image);
        }
      }
      this._updateHoverImage(variant);
    }

    /**
     * Hover image swap. Fallback order:
     *   1. `variant.hoverImage` — second metafield image (distinct hover)
     *      or, for single-image variants, the same URL as the primary so
     *      the cross-fade is a visual no-op. Liquid collapses these two
     *      cases so JS always receives a variant-specific src when one
     *      exists.
     *   2. `this.defaultHoverSrc` — product-level lifestyle shot captured
     *      at boot, used when no variant is selected or the matched
     *      variant has no image data at all.
     */
    _updateHoverImage(variant) {
      if (!this.hoverImageEl) return;
      const target = (variant && variant.hoverImage) || this.defaultHoverSrc || '';
      if (!target) return;
      if (this.hoverImageEl.getAttribute('src') !== target) {
        this.hoverImageEl.setAttribute('src', target);
      }
    }

    _updatePrice(variant, pricing) {
      if (!variant || !this.priceEl) return;
      const computed = pricing || this._computePricing(variant);
      if (!computed) return;

      this.priceEl.classList.toggle('dd-card2__price--on-sale', computed.onSale);

      // Rebuild the price DOM in place so regular ↔ sale layout switches cleanly.
      if (computed.onSale) {
        this.priceEl.innerHTML = `
          <span class="dd-card2__price-compare" data-price-compare>${computed.compareText}</span>
          <span class="dd-card2__price-sale" data-price-value>${computed.saleText}</span>
        `;
      } else {
        this.priceEl.innerHTML = `
          <span class="dd-card2__price-regular" data-price-value>${computed.saleText || variant.price}</span>
        `;
      }
    }

    _updateColorLabel() {
      if (!this.colorPosition) return;
      const name = this.selection[this.colorPosition];
      if (this.colorLabelEl) {
        this.colorLabelEl.textContent = name || '—';
      }
      // Image overlay badge — only visible once a color is picked so the
      // unselected state stays clean.
      if (this.colorBadgeEl) {
        if (name) {
          this.colorBadgeEl.textContent = name;
          this.colorBadgeEl.removeAttribute('hidden');
        } else {
          this.colorBadgeEl.setAttribute('hidden', '');
          this.colorBadgeEl.textContent = '';
        }
      }
    }

    _updateCta(variant) {
      if (!this.ctaEl || !this.ctaLabelEl) return;
      const complete = this._isSelectionComplete();

      // "Select Options" — user hasn't picked all required options yet.
      // This is a *neutral* disabled state ("you have something to do"),
      // styled in CSS as the gray pill. Keep `is-unavailable` off so the
      // destructive red treatment doesn't bleed into a recoverable state.
      if (!complete) {
        this.ctaEl.setAttribute('disabled', '');
        this.ctaEl.setAttribute('aria-disabled', 'true');
        this.ctaEl.classList.add('is-disabled');
        this.ctaEl.classList.remove('is-unavailable');
        this.ctaLabelEl.textContent = this.labels.select;
        return;
      }

      // "Unavailable" — selection is complete but the matched variant is
      // out of stock (or no matching variant exists). This is a *terminal*
      // disabled state (nothing the user can do here), so we layer
      // `is-unavailable` on top of `is-disabled` to swap the pill from
      // neutral gray to destructive red — matches the existing
      // `--dd-error-*` semantic tokens used in mini-cart errors and the
      // invalid-discount pill, so the visual language is consistent
      // across the cart/checkout journey.
      if (!variant || !variant.available) {
        this.ctaEl.setAttribute('disabled', '');
        this.ctaEl.setAttribute('aria-disabled', 'true');
        this.ctaEl.classList.add('is-disabled');
        this.ctaEl.classList.add('is-unavailable');
        this.ctaLabelEl.textContent = this.labels.unavailable;
        return;
      }

      // Enabled "Add to cart" — drop both disabled-flavour classes so the
      // base black-pill styling re-applies cleanly.
      this.ctaEl.removeAttribute('disabled');
      this.ctaEl.setAttribute('aria-disabled', 'false');
      this.ctaEl.classList.remove('is-disabled');
      this.ctaEl.classList.remove('is-unavailable');
      this.ctaLabelEl.textContent = this.labels.add;
    }

    async _addToCart() {
      const variant = this._matchVariant();
      if (!variant || !variant.available || !this._isSelectionComplete()) return;

      /* Bundle-mode short-circuit. When the host element carries
         `data-dd-cta-mode="bundle"` (set by callers like the
         inventory-builder section via `dd-product-card-expanded`'s
         optional `cta_mode` param), the card MUST NOT fetch
         `/cart/add.js` — it stages the selection into the parent
         feature's in-memory state instead. We dispatch a bubbling
         `dd-card2:add-to-bundle` CustomEvent carrying the resolved
         variant + qty so the listener (e.g. `dd-bundle-builder.js`)
         can absorb it without touching internal card state.

         Default mode is `'cart'` (or any other value / absent attr)
         which preserves the legacy POST-then-open-drawer flow below
         byte-for-byte. */
      const ctaMode = this.dataset.ddCtaMode || 'cart';
      if (ctaMode === 'bundle') {
        const qty = Math.max(1, Number(this.quantity) || 1);
        let imageUrl = '';
        if (variant.image) {
          imageUrl = variant.image;
        } else if (this.heroEl) {
          imageUrl = this.heroEl.getAttribute('src') || '';
        }
        /* Resolve the card's customer-facing price (Regios-aware) so
           the bundle panel can render the same strikethrough+sale UI
           the card itself shows. _computePricing reconciles MSRP
           (compare_at_price) with any in-flight Regios entry and
           returns { saleCents, compareCents } that match the card's
           paint. Falls back to priceCents when Regios hasn't
           resolved yet — the line will then render as a flat
           (non-discounted) price, which is the safe default. */
        const pricing = this._computePricing(variant) || null;
        const saleCents = pricing && pricing.saleCents != null
          ? pricing.saleCents
          : variant.priceCents;
        const compareAtCents = pricing && pricing.compareCents != null
          ? pricing.compareCents
          : null;
        try {
          /* Dispatch on `this` (not `document`) so the bubbling event
             carries `e.target === <dd-card2>` up to the document-level
             listener. The bundle builder's section-ownership filter
             does `self.el.contains(e.target)` to confirm the card
             lives in its grid before absorbing the add; dispatching
             on `document` would set e.target=document and the filter
             would reject every event silently. The event still
             reaches `document` because `bubbles: true` is set. */
          this.dispatchEvent(
            new CustomEvent('dd-card2:add-to-bundle', {
              bubbles: true,
              detail: {
                variant: {
                  id: variant.id,
                  options: variant.options,
                  price: variant.price,
                  priceCents: variant.priceCents,
                  /* compareAtCents is the gross/MSRP for strikethrough;
                     saleCents is the actual customer-facing price (VIP
                     applied via Regios). Both are stamped at add-time
                     and persist through reloads via localStorage. */
                  compareAtCents,
                  saleCents,
                  available: variant.available,
                  image: imageUrl,
                },
                quantity: qty,
                productId: this.productId,
                productHandle: (this.dataset.url || '').replace(/^.*\/products\//, '').replace(/[?#].*$/, ''),
                productTitle: this.getAttribute('aria-label') || (this.heroEl && this.heroEl.getAttribute('alt')) || '',
                imageUrl,
                source: 'dd-card2',
              },
            })
          );
        } catch (_e) {
          /* non-fatal: bundle host may not be present yet */
        }
        /* Brief CTA flash so the user knows the click registered. */
        this.ctaLabelEl.textContent = ADDED_LABEL;
        this.ctaEl.classList.add('is-added');
        setTimeout(() => {
          this.ctaEl.classList.remove('is-added');
          this.ctaLabelEl.textContent = this.labels.add;
        }, 1600);
        return;
      }

      const api = window.DDCartApi;
      this.ctaLabelEl.textContent = ADDING_LABEL;
      this.ctaEl.setAttribute('disabled', '');

      // Request the cart-drawer section HTML alongside the add so we can
      // hand it to <cart-drawer>.renderContents() in one shot — matches the
      // pattern used by `dd-sticky-footer.js` and keeps the mini-cart in
      // lockstep with the rest of the theme (single paint, no extra fetch).
      const sections = 'cart-drawer,cart-icon-bubble';

      const qty = Math.max(1, Number(this.quantity) || 1);

      try {
        let result;
        if (api && typeof api.add === 'function') {
          result = await api.add(variant.id, qty, sections);
        } else {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{ id: variant.id, quantity: qty }],
              sections,
            }),
          });
          if (!res.ok) throw new Error('cart/add failed: ' + res.status);
          result = await res.json();
        }

        this.ctaLabelEl.textContent = ADDED_LABEL;
        this.ctaEl.classList.add('is-added');

        // Render the freshly-returned cart-drawer section into the live
        // <cart-drawer> and open it. renderContents() both swaps the HTML
        // and toggles the drawer open on Dawn's custom element. If it
        // isn't available (older theme builds) fall back to .open().
        try {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) {
            if (result && result.sections && typeof drawer.renderContents === 'function') {
              drawer.renderContents(result);
            } else if (typeof drawer.open === 'function') {
              drawer.open(this.ctaEl);
            }
          }
        } catch (_e) {
          /* non-fatal: drawer open is best-effort */
        }

        try {
          document.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'dd-card2', result } })
          );
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (_e) {}

        // Revert the CTA after a short beat so shoppers can add another variant.
        setTimeout(() => {
          this.ctaEl.classList.remove('is-added');
          this.ctaEl.removeAttribute('disabled');
          this.ctaLabelEl.textContent = this.labels.add;
        }, 1600);
      } catch (err) {
        console.warn('[dd-card2] add-to-cart failed', err);
        this.ctaLabelEl.textContent = 'Try again';
        this.ctaEl.classList.remove('is-added');
        this.ctaEl.removeAttribute('disabled');
      }
    }
  }

  window.customElements.define('dd-card2', DdCard2);
})();
